import { memo, useState, useEffect } from 'react'
import { Template } from './Template'
import type { KcProps } from 'keycloakify'
import { useKcMessage } from 'keycloakify/lib/i18n/useKcMessage'
import type { KcContext } from '../kcContext'
import { useCssAndCx } from 'tss-react'
import { Button, Input, Form } from '@xanadu_ai/xanadu-component-library'

//This is a copy paste from https://github.com/InseeFrLab/keycloakify/blob/main/src/lib/components/Register.tsx
//It is now up to us to implement a special behavior to leverage the non standard authorizedMailDomains
//provided by the plugin: https://github.com/micedre/keycloak-mail-whitelisting installed on our keycloak server.

type KcContext_Register = Extract<KcContext, { pageId: 'register.ftl' }>

export const Register = memo(
  ({ kcContext, ...props }: { kcContext: KcContext_Register } & KcProps) => {
    const {
      url,
      messagesPerField,
      register,
      realm,
      passwordRequired,
      recaptchaRequired,
      recaptchaSiteKey,
    } = kcContext

    const [userFirstName, setUserFirstName] = useState<string>('')
    const [userLastName, setUserLastName] = useState<string>('')
    const [userEmail, setUserEmail] = useState<string>('')
    const [username, setUsername] = useState<string>('')
    const [userPassword, setUserPassword] = useState<string>('')
    const [userPasswordConfirm, setUserPasswordConfirm] = useState<string>('')
    const [submitDisabled, toggleSubmitDisabled] = useState<boolean>(true)

    useEffect(() => {
      register.formData = {
        firstName: userFirstName,
        lastName: userLastName,
        email: userEmail,
        username: username,
      }
    }, [register, userFirstName, userLastName, userEmail, username])

    useEffect(() => {
      toggleSubmitDisabled(
        !userFirstName ||
          !userLastName ||
          (!realm.registrationEmailAsUsername && !username) ||
          !userEmail ||
          !userPassword ||
          !userPasswordConfirm
      )
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      userFirstName,
      userLastName,
      userEmail,
      username,
      userPassword,
      userPasswordConfirm,
    ])

    const { msg, msgStr } = useKcMessage()

    const { cx } = useCssAndCx()

    return (
      <Template
        {...{ kcContext, ...props }}
        doFetchDefaultThemeResources={false}
        headerNode="Sign up for Xanadu Cloud"
        headerDescription="Join a community of learners, researchers, and developers to build the next generation of quantum technology."
        formNode={
          <Form
            formName="kc-register-form"
            action={url.registrationAction}
            method="post"
            onSubmit={(_e: React.SyntheticEvent) => {}}
          >
            <>
              <div
                className={cx(
                  props.kcFormGroupClass,
                  messagesPerField.printIfExists(
                    'firstName',
                    props.kcFormGroupErrorClass
                  )
                )}
              >
                <div className={cx(props.kcInputWrapperClass)}>
                  <Input
                    formName="kc-register-form"
                    fieldName="firstName"
                    fieldLabel="First name"
                    testId="first-name"
                    className={cx(props.kcInputClass)}
                    type="text"
                    value={userFirstName}
                    onChange={(value: string) => setUserFirstName(value)}
                  />
                </div>
              </div>

              <div
                className={cx(
                  props.kcFormGroupClass,
                  messagesPerField.printIfExists(
                    'lastName',
                    props.kcFormGroupErrorClass
                  )
                )}
              >
                <div className={cx(props.kcInputWrapperClass)}>
                  <Input
                    formName="kc-register-form"
                    fieldName="lastName"
                    fieldLabel="Last name"
                    testId="last-name"
                    className={cx(props.kcInputClass)}
                    type="text"
                    value={userLastName}
                    onChange={(value: string) => setUserLastName(value)}
                  />
                </div>
              </div>

              <div
                className={cx(
                  props.kcFormGroupClass,
                  messagesPerField.printIfExists(
                    'email',
                    props.kcFormGroupErrorClass
                  )
                )}
              >
                <div className={cx(props.kcInputWrapperClass)}>
                  <Input
                    formName="kc-register-form"
                    fieldName="email"
                    fieldLabel="Email"
                    testId="email"
                    className={cx(props.kcInputClass)}
                    type="text"
                    value={userEmail}
                    onChange={(value: string) => setUserEmail(value)}
                  />
                </div>
              </div>
              {!realm.registrationEmailAsUsername && (
                <div
                  className={cx(
                    props.kcFormGroupClass,
                    messagesPerField.printIfExists(
                      'username',
                      props.kcFormGroupErrorClass
                    )
                  )}
                >
                  <div className={cx(props.kcInputWrapperClass)}>
                    <Input
                      formName="kc-register-form"
                      fieldName="username"
                      fieldLabel="Username"
                      testId="username"
                      className={cx(props.kcInputClass)}
                      type="text"
                      value={username}
                      onChange={(value: string) => setUsername(value)}
                    />
                  </div>
                </div>
              )}
              {passwordRequired && (
                <>
                  <div
                    className={cx(
                      props.kcFormGroupClass,
                      messagesPerField.printIfExists(
                        'password',
                        props.kcFormGroupErrorClass
                      )
                    )}
                  >
                    <div className={cx(props.kcInputWrapperClass)}>
                      <Input
                        formName="kc-register-form"
                        fieldName="password"
                        fieldLabel="Password"
                        testId="password"
                        className={cx(props.kcInputClass)}
                        type="password"
                        value={userPassword}
                        onChange={(value: string) => setUserPassword(value)}
                      />
                    </div>
                  </div>

                  <div
                    className={cx(
                      props.kcFormGroupClass,
                      messagesPerField.printIfExists(
                        'password-confirm',
                        props.kcFormGroupErrorClass
                      )
                    )}
                  >
                    <div className={cx(props.kcInputWrapperClass)}>
                      <Input
                        formName="kc-register-form"
                        fieldName="password-confirm"
                        fieldLabel="Confirm password"
                        testId="password-confirm"
                        className={cx(props.kcInputClass)}
                        type="password"
                        value={userPasswordConfirm}
                        onChange={(value: string) =>
                          setUserPasswordConfirm(value)
                        }
                      />
                    </div>
                  </div>
                </>
              )}
              {recaptchaRequired && (
                <div className="form-group">
                  <div className={cx(props.kcInputWrapperClass)}>
                    <div
                      className="g-recaptcha mb-4"
                      data-size="normal"
                      data-sitekey={recaptchaSiteKey}
                    ></div>
                  </div>
                </div>
              )}
              <div className={cx(props.kcFormGroupClass)}>
                <div
                  id="kc-form-options"
                  className={cx(props.kcFormOptionsClass)}
                >
                  <div className={cx(props.kcFormOptionsWrapperClass)}>
                    <span>
                      <a
                        href={url.loginUrl}
                        id="back-to-login"
                        data-testid="back-to-login"
                      >
                        {msg('backToLogin')}
                      </a>
                    </span>
                  </div>
                </div>

                <div className="mt-4"><p>By signing up, you agree to Xanadu's <a href="https://xanadu.ai/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>.</p></div>

                <div
                  id="kc-form-buttons"
                  className={cx(props.kcFormButtonsClass)}
                >
                  <Button
                    data-testid="signup-button"
                    className="justify-center"
                    buttonType="submit"
                    disabled={submitDisabled}
                    label={msgStr('doRegister')}
                  />
                </div>
              </div>
            </>
          </Form>
        }
      />
    )
  }
)
